import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { invitations, auth } from '../api';
import pricingConfig from '../config/pricing.json';
import { useToast } from '../components/Toast';
import { stripEmoji } from '../utils/text';

function TestResults() {
  const { invitationId } = useParams();
  const navigate = useNavigate();
  const showToast = useToast();
  const [results, setResults] = useState(null);
  const [checks, setChecks] = useState({});
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [showWeightDescription, setShowWeightDescription] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [balance, setBalance] = useState(0);
  const [paying, setPaying] = useState(false);
  const [feedbackCopied, setFeedbackCopied] = useState(false);

  useEffect(() => {
    loadBalance();
    loadResults();
  }, [invitationId]);

  const checkAnswer = (question) => {
    if (question.question_type === 'single') {
      return question.correct_answer === question.candidate_answer;
    } else if (question.question_type === 'multiple') {
      const correct = new Set(question.correct_answer.split(','));
      const candidate = new Set(question.candidate_answer.split(','));
      return correct.size === candidate.size && 
             [...correct].every(val => candidate.has(val));
    }
    return false;
  };

  const calculateMultipleChoicePoints = (question) => {
    if (question.question_type !== 'multiple' || !question.correct_answer || !question.candidate_answer) {
      return 0;
    }
    
    const correct = new Set(question.correct_answer.split(',').map(v => v.trim()));
    const candidate = new Set(question.candidate_answer.split(',').map(v => v.trim()));
    
    // Если выбрал хотя бы один неправильный вариант - 0 баллов
    const hasWrongAnswer = [...candidate].some(val => !correct.has(val));
    if (hasWrongAnswer) {
      return 0;
    }
    
    // Если выбрал все правильные варианты - полные баллы
    if (correct.size === candidate.size && [...correct].every(val => candidate.has(val))) {
      return question.weight;
    }
    
    // Частичные баллы: округленное вниз ((максимальное кол-во баллов / общее кол-во правильных вариантов) * количество выбранных правильных вариантов)
    const selectedCorrectCount = [...candidate].filter(val => correct.has(val)).length;
    const totalCorrectCount = correct.size;
    
    if (totalCorrectCount === 0) {
      return 0;
    }
    
    const partialPoints = Math.floor((question.weight / totalCorrectCount) * selectedCorrectCount);
    return partialPoints;
  };

  const loadBalance = async () => {
    try {
      const response = await auth.getMe();
      setBalance(response.data.balance || 0);
    } catch (err) {
      console.error('Error loading balance:', err);
    }
  };

  const loadResults = async () => {
    try {
      const response = await invitations.getResults(invitationId);
      setResults(response.data);
      
      // Проверяем, оплачен ли просмотр результатов
      if (!response.data.results_viewed) {
        // Если не оплачен, показываем модальное окно оплаты
        setShowPaymentModal(true);
        setLoading(false);
        return;
      }
      
      // Check if status is already set (passed/failed/deferred) - make read-only
      const invitationStatus = response.data.status || 'completed';
      setCurrentStatus(invitationStatus);
      const isStatusFinal = ['passed', 'failed', 'deferred', 'timeout'].includes(invitationStatus);
      setIsReadOnly(isStatusFinal);
      
      // Initialize checks with automatic verification (or from manual_checks if exists)
      const initialChecks = {};
      
      // If manual checks exist (status was set), use them
      if (response.data.manual_checks) {
        try {
          const savedChecks = JSON.parse(response.data.manual_checks);
          const savedCheckIds = new Set(savedChecks.map(c => c.question_id));
          
          savedChecks.forEach(check => {
            initialChecks[check.question_id] = {
              is_correct: check.is_correct,
              weight: check.weight,
              earned_points: check.earned_points !== undefined ? check.earned_points : (check.is_correct ? check.weight : 0),
              auto: false,
              section: stripEmoji(response.data.questions.find(q => q.question_id === check.question_id)?.section)
            };
          });
          
          // Для вопросов, которых нет в сохраненных checks, создаем записи с автоматической проверкой
          response.data.questions.forEach(q => {
            if (!savedCheckIds.has(q.question_id)) {
              let isCorrect = false;
              let earnedPoints = 0;
              
              if (q.question_type === 'single') {
                isCorrect = checkAnswer(q);
                earnedPoints = isCorrect ? q.weight : 0;
              } else if (q.question_type === 'multiple') {
                earnedPoints = calculateMultipleChoicePoints(q);
                isCorrect = earnedPoints === q.weight;
              } else {
                isCorrect = false;
                earnedPoints = 0;
              }
              
              initialChecks[q.question_id] = {
                is_correct: isCorrect,
                weight: q.weight,
                earned_points: earnedPoints,
                auto: q.question_type === 'single',
                section: stripEmoji(q.section)
              };
            }
          });
        } catch (e) {
          console.error('Failed to parse manual checks', e);
        }
      } else {
        // Auto-check
        response.data.questions.forEach(q => {
          let isCorrect = false;
          let earnedPoints = 0;
          
          if (q.question_type === 'single') {
            // Для single вопросов - автоматическая проверка
            isCorrect = checkAnswer(q);
            earnedPoints = isCorrect ? q.weight : 0;
          } else if (q.question_type === 'multiple') {
            // Для multiple вопросов - частичные баллы
            earnedPoints = calculateMultipleChoicePoints(q);
            isCorrect = earnedPoints === q.weight;
          } else {
            // Для text вопросов - по умолчанию 0, менеджер должен проверить вручную
            isCorrect = false;
            earnedPoints = 0;
          }
          
          initialChecks[q.question_id] = {
            is_correct: isCorrect,
            weight: q.weight,
            earned_points: earnedPoints,
            // Только single вопросы автоматические, text и multiple - редактируемые
            auto: q.question_type === 'single',
            section: stripEmoji(q.section)
          };
        });
      }
      
      setChecks(initialChecks);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load results', err);
      if (err.response?.status !== 404) {
        // Сетевая/серверная ошибка — показываем баннер с кнопкой «Повторить»
        setLoadError(true);
        showToast('Ошибка загрузки результатов', 'error');
      }
      setLoading(false);
    }
  };

  const handlePayForResults = async () => {
    if (!results) return;
    
    const viewPrice = results.view_price || pricingConfig.candidate_test_price || 100;
    
    // Проверяем баланс
    if (balance < viewPrice) {
      // Недостаточно средств - показываем сообщение и редирект на оплату
      showToast(`На вашем балансе ${balance} руб., а для просмотра результатов этого кандидата необходимо ${viewPrice} руб.`, 'error');
      navigate('/payment?showForm=true');
      return;
    }
    
    setPaying(true);
    try {
      await invitations.payForResults(invitationId);
      // Обновляем баланс
      await loadBalance();
      // Закрываем модальное окно и перезагружаем результаты
      setShowPaymentModal(false);
      await loadResults();
    } catch (err) {
      console.error('Failed to pay for results', err);
      const errorMessage = err.response?.data?.detail || 'Ошибка при оплате просмотра результатов';
      if (errorMessage.includes('Insufficient balance')) {
        showToast(`На вашем балансе ${balance} руб., а для просмотра результатов этого кандидата необходимо ${viewPrice} руб.`, 'error');
        navigate('/payment?showForm=true');
      } else {
        showToast(errorMessage, 'error');
      }
    } finally {
      setPaying(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const toggleCheck = (questionId) => {
    if (isReadOnly) return; // Don't allow editing if status is final
    
    setChecks(prev => {
      const newIsCorrect = !prev[questionId].is_correct;
      return {
        ...prev,
        [questionId]: {
          ...prev[questionId],
          is_correct: newIsCorrect,
          earned_points: newIsCorrect ? prev[questionId].weight : 0
        }
      };
    });
  };

  const updateEarnedPoints = (questionId, value) => {
    if (isReadOnly) return;
    
    const maxWeight = checks[questionId]?.weight || 0;
    // Для текстовых вопросов можно ставить любую оценку от 0 до максимума
    let numValue = parseInt(value) || 0;
    // Ограничиваем значение в пределах от 0 до максимума за вопрос
    numValue = Math.max(0, Math.min(numValue, maxWeight));
    
    setChecks(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        earned_points: numValue,
        is_correct: numValue > 0
      }
    }));
  };

  const calculateScore = () => {
    let total = 0;
    let max = 0;
    Object.values(checks).forEach(check => {
      total += check.earned_points || 0;
      max += check.weight;
    });
    return { total, max, percentage: max > 0 ? Math.round((total / max) * 100) : 0 };
  };

  const calculateSectionScores = () => {
    const sections = {};
    results.questions.forEach(q => {
      const section = q.section;
      if (!sections[section]) {
        sections[section] = { total: 0, max: 0, correct: 0 };
      }
      sections[section].max += q.weight;
      const earnedPoints = checks[q.question_id]?.earned_points || 0;
      sections[section].total += earnedPoints;
      if (earnedPoints > 0) {
        sections[section].correct++;
      }
    });

    // Calculate percentages
    Object.keys(sections).forEach(section => {
      sections[section].percentage = sections[section].max > 0 
        ? Math.round((sections[section].total / sections[section].max) * 100) 
        : 0;
    });

    return sections;
  };

  const generateFeedback = (statusType) => {
    const sectionScores = calculateSectionScores();
    const excellent = [];
    const good = [];
    const needsWork = [];
    const poor = [];

    Object.entries(sectionScores).forEach(([section, data]) => {
      const pct = data.percentage;
      if (pct === 100) {
        excellent.push(section);
      } else if (pct >= 80) {
        good.push(section);
      } else if (pct >= 60) {
        needsWork.push(section);
      } else {
        poor.push(section);
      }
    });

    let feedback = `Здравствуйте!\n\n`;
    feedback += `Спасибо, что прошли тестирование и уделили нам время.\n\n`;

    // Начинаем с положительных моментов
    if (excellent.length > 0) {
      feedback += `У Вас отличный результат в ${excellent.length === 1 ? 'разделе' : 'разделах'} "${excellent.join('", "')}".\n\n`;
    }

    if (good.length > 0) {
      feedback += `Хороший уровень — в ${good.length === 1 ? 'разделе' : 'разделах'} "${good.join('", "')}".\n\n`;
    }

    // Деликатно указываем на области для развития
    if (needsWork.length > 0) {
      feedback += `Рекомендуем подтянуть ${needsWork.length === 1 ? 'раздел' : 'разделы'} "${needsWork.join('", "')}".\n\n`;
    }

    if (poor.length > 0) {
      feedback += `${poor.length === 1 ? 'Раздел' : 'Разделы'} "${poor.join('", "')}" стоит изучить глубже — здесь результат заметно ниже остальных.\n\n`;
    }

    // Добавляем заключительное сообщение в зависимости от статуса
    if (statusType === 'passed') {
      feedback += `По итогам теста приглашаем Вас на следующий этап отбора. Детали пришлёт наш HR-специалист в ближайшее время.\n\n`;
    } else if (statusType === 'failed') {
      feedback += `К сожалению, сейчас результата недостаточно для этой позиции. Будем рады увидеть Вас снова, когда подтянете отмеченные темы.\n\n`;
    } else if (statusType === 'deferred') {
      feedback += `На эту позицию мы не готовы пригласить Вас дальше, но передали Ваше резюме в другие команды. Если появится подходящая вакансия, с Вами свяжется HR-специалист.\n\n`;
    }

    feedback += `Успехов!\n\n`;
    feedback += `С уважением,\nкоманда найма`;

    return feedback;
  };

  const handleCopyFeedback = async () => {
    try {
      await navigator.clipboard.writeText(feedbackText);
      setFeedbackCopied(true);
      setTimeout(() => {
        setFeedbackCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy feedback:', err);
      // Fallback для старых браузеров
      const textArea = document.createElement('textarea');
      textArea.value = feedbackText;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setFeedbackCopied(true);
        setTimeout(() => {
          setFeedbackCopied(false);
        }, 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleStatusClick = (statusType) => {
    setSelectedStatus(statusType);
    const feedback = generateFeedback(statusType);
    setFeedbackText(feedback);
    setShowFeedback(true);
  };

  const handleConfirmStatus = async () => {
    if (!selectedStatus) {
      showToast('Пожалуйста, выберите статус', 'error');
      return;
    }
    
    setSaving(true);
    try {
      const checksArray = results.questions.map(q => ({
        question_id: q.question_id,
        is_correct: checks[q.question_id]?.is_correct || false,
        weight: q.weight,
        earned_points: checks[q.question_id]?.earned_points || 0
      }));

      
      await invitations.updateStatus(invitationId, {
        status: selectedStatus,
        manual_checks: JSON.stringify(checksArray),
        feedback: feedbackText
      });

      setShowFeedback(false);
      setSelectedStatus(null);
      // Перезагружаем результаты для обновления статуса
      await loadResults();
    } catch (err) {
      console.error('Failed to save status', err);
      showToast('Ошибка сохранения: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  const exportResults = (format) => {
    const score = calculateScore();
    
    if (format === 'txt') {
      exportTXT(score);
    } else if (format === 'csv') {
      exportCSV(score);
    }
    
    setShowExportMenu(false);
  };

  const exportTXT = (score) => {
    let text = `РЕЗУЛЬТАТЫ ТЕСТИРОВАНИЯ\n`;
    text += `${'='.repeat(80)}\n`;
    text += `Кандидат: ${results.candidate_title}\n`;
    text += `Тест: ${results.test_name}\n`;
    text += `Дата: ${new Date(results.completed_at).toLocaleString('ru-RU')}\n`;
    text += `Баллы: ${score.total} из ${score.max} (${score.percentage}%)\n`;
    text += `${'='.repeat(80)}\n\n`;

    results.questions.forEach((q, idx) => {
      const isCorrect = checks[q.question_id]?.is_correct;
      const earnedPoints = checks[q.question_id]?.earned_points || 0;
      text += `${idx + 1}. ${q.section} | Баллы: ${earnedPoints}/${q.weight}\n`;
      text += `Вопрос: ${q.question_text}\n`;
      
      if (q.question_type !== 'text' && q.options) {
        const options = JSON.parse(q.options);
        text += `Варианты: ${options.join(', ')}\n`;
      }
      
      text += `Правильный ответ: ${formatAnswer(q.correct_answer, q.question_type, q.options)}\n`;
      text += `Ответ кандидата: ${formatAnswer(q.candidate_answer, q.question_type, q.options)}\n`;
      text += `Результат: ${isCorrect ? 'Правильно' : 'Неправильно'}\n`;
      if (q.weight_description) {
        text += `Обоснование оценки: ${q.weight_description}\n`;
      }
      text += `${'-'.repeat(80)}\n\n`;
    });

    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `results_${results.candidate_title}_${Date.now()}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportCSV = (score) => {
    let csv = 'Номер,Секция,Вопрос,Правильный ответ,Ответ кандидата,Результат,Баллы за правильный ответ,Баллы получено,Обоснование оценки\n';
    
    results.questions.forEach((q, idx) => {
      const isCorrect = checks[q.question_id]?.is_correct ? 'Правильно' : 'Неправильно';
      const earnedPoints = checks[q.question_id]?.earned_points || 0;
      const row = [
        idx + 1,
        q.section,
        `"${q.question_text.replace(/"/g, '""')}"`,
        `"${formatAnswer(q.correct_answer, q.question_type, q.options).replace(/"/g, '""')}"`,
        `"${formatAnswer(q.candidate_answer, q.question_type, q.options).replace(/"/g, '""')}"`,
        isCorrect,
        q.weight,
        earnedPoints,
        `"${(q.weight_description || '').replace(/"/g, '""')}"`
      ];
      csv += row.join(',') + '\n';
    });

    csv += `\nИтого:,,,,,${score.total}/${score.max} (${score.percentage}%),\n`;

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `results_${results.candidate_title}_${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const formatAnswer = (answer, type, optionsJson) => {
    if (!answer) return '(Нет ответа)';
    
    if (type === 'text') {
      return answer;
    }
    
    try {
      const options = JSON.parse(optionsJson || '[]');
      const indices = answer.split(',').map(i => parseInt(i.trim()));
      return indices.map(i => options[i] || i).join(', ');
    } catch {
      return answer;
    }
  };

  if (loading) {
    return (
      <>
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Загрузка результатов...</p>
        </div>
      </>
    );
  }

  if (loadError && !results) {
    return (
      <>
        <div className="card">
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 flex items-center justify-between gap-4">
            <span>Не удалось загрузить результаты</span>
            <button
              className="btn"
              onClick={() => {
                setLoadError(false);
                setLoading(true);
                loadResults();
              }}
            >
              Повторить
            </button>
          </div>
        </div>
      </>
    );
  }

  if (!results) {
    return (
      <>
        <div className="card">
          <p>Результаты не найдены</p>
        </div>
      </>
    );
  }

  // Модальное окно оплаты просмотра результатов
  if (showPaymentModal && results && !results.results_viewed) {
    const viewPrice = results.view_price || pricingConfig.candidate_test_price || 100;
    const hasEnoughBalance = balance >= viewPrice;
    
    return (
      <>
        {createPortal(
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: 'bold',
                marginBottom: '16px',
                color: '#111827'
              }}>
                Оплата просмотра результатов
              </h2>
              
              {hasEnoughBalance ? (
                <>
                  <p style={{ marginBottom: '12px', color: '#374151', fontSize: '16px', lineHeight: '1.6' }}>
                    Ваш текущий баланс составляет <strong style={{ color: '#111827' }}>{formatAmount(balance)}</strong>
                  </p>
                  <p style={{ marginBottom: '24px', color: '#374151', fontSize: '16px', lineHeight: '1.6' }}>
                    Для просмотра результатов этого кандидата с вашего баланса будет снято <strong style={{ color: '#111827' }}>{formatAmount(viewPrice)}</strong>
                  </p>
                  
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={handlePayForResults}
                      disabled={paying}
                      style={{
                        flex: 1,
                        backgroundColor: '#1e73e8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px 24px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: paying ? 'not-allowed' : 'pointer',
                        opacity: paying ? 0.6 : 1,
                        transition: 'all 0.2s'
                      }}
                    >
                      {paying ? 'Обработка...' : 'Продолжить'}
                    </button>
                    <button
                      onClick={() => navigate(-1)}
                      disabled={paying}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: paying ? 'not-allowed' : 'pointer',
                        opacity: paying ? 0.6 : 1
                      }}
                    >
                      Отмена
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{
                    backgroundColor: '#fef3c7',
                    border: '1px solid #fbbf24',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '24px'
                  }}>
                    <p style={{ margin: 0, color: '#92400e', fontSize: '16px', lineHeight: '1.6' }}>
                      <strong>Недостаточно средств</strong>
                    </p>
                    <p style={{ margin: '8px 0 0 0', color: '#78350f', fontSize: '14px' }}>
                      На вашем балансе <strong>{formatAmount(balance)}</strong>, а для просмотра результатов этого кандидата необходимо <strong>{formatAmount(viewPrice)}</strong>
                    </p>
                  </div>
                  
                  <button
                    onClick={() => navigate('/payment?showForm=true')}
                    style={{
                      width: '100%',
                      backgroundColor: '#1e73e8',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    Пополнить баланс
                  </button>
                </>
              )}
            </div>
          </div>
          , document.body
        )}
      </>
    );
  }

  const score = calculateScore();

  return (
    <>
      <div className="header">
        <div>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'none',
              border: 'none',
              color: '#2d8cff',
              cursor: 'pointer',
              marginBottom: '8px',
              fontSize: '14px',
            }}
          >
            ← Назад
          </button>
          <h1 style={{ fontSize: '24px', marginBottom: '8px' }}>Результаты тестирования</h1>
          <p style={{ color: '#666', marginTop: '8px', fontSize: '14px', lineHeight: '1.5' }}>
            <span style={{ display: 'block', marginBottom: '4px' }}>Кандидат: <strong>{results.candidate_title}</strong></span>
            <span>Тест: <strong>{results.test_name}</strong></span>
          </p>
          {results.preliminary_resolution ? (
            <div style={{ marginTop: '12px' }}>
              <span
                className={`inline-block px-3 py-1.5 rounded-lg text-sm font-semibold ${
                  results.preliminary_resolution === 'failed'
                    ? 'bg-red-100 text-red-800 border-2 border-red-300'
                    : 'bg-yellow-100 text-yellow-800 border-2 border-yellow-300'
                }`}
                title={
                  results.preliminary_resolution === 'failed'
                    ? 'Предварительная резолюция: не прошел (автоматическая проверка системы)'
                    : 'Предварительная резолюция: под сомнением (автоматическая проверка системы)'
                }
              >
                {results.preliminary_resolution === 'failed' ? 'Предпроверено - Не прошел' : 'Предпроверено - Под сомнением'}
              </span>
            </div>
          ) : results.completed_at ? (
            <div style={{ marginTop: '12px' }}>
              <span
                className="inline-block px-3 py-1.5 rounded-lg text-sm font-semibold bg-green-100 text-green-800 border-2 border-green-300"
                title="Предварительная проверка пройдена успешно (автоматическая проверка системы)"
              >
                Предпроверено - OK
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {/* Итоговый результат с кнопками */}
      <div className="card" style={{ marginBottom: '20px', background: '#f0f4ff' }}>
        <h2 style={{ marginBottom: '12px', color: '#2d8cff', fontSize: '18px', lineHeight: '1.4' }}>
          Итоговый результат: {score.total} / {score.max} баллов ({score.percentage}%)
        </h2>

        {isReadOnly ? (
          // Read-only mode - show final status
          <div style={{
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '2px solid #2d8cff'
          }}>
            <div style={{
              padding: '20px',
              borderRadius: '12px',
              background: currentStatus === 'passed' ? '#c6f6d5' :
                          currentStatus === 'failed' ? '#fed7d7' :
                          currentStatus === 'deferred' ? '#feebc8' :
                          currentStatus === 'timeout' ? '#feebc8' : '#e2e8f0',
              textAlign: 'center'
            }}>
              <h3 style={{
                fontSize: '20px',
                color: currentStatus === 'passed' ? '#22543d' :
                        currentStatus === 'failed' ? '#742a2a' :
                        currentStatus === 'deferred' ? '#744210' :
                        currentStatus === 'timeout' ? '#744210' : '#1a202c',
                margin: 0
              }}>
                {currentStatus === 'passed' ? 'Кандидат прошел' :
                 currentStatus === 'failed' ? 'Кандидат не прошел' :
                 currentStatus === 'deferred' ? 'Кандидат отложен' :
                 currentStatus === 'timeout' ? 'Тайм-аут' : 'Ожидает проверки'}
              </h3>
              <p style={{
                marginTop: '8px',
                color: '#666',
                fontSize: '14px'
              }}>
                Решение зафиксировано. Редактирование недоступно.
              </p>
            </div>
          </div>
        ) : (
          // Editable mode - show action buttons
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '2px solid #2d8cff'
          }} className="md:flex-row">
            <button
              className="btn"
              style={{
                flex: 1,
                background: '#48bb78',
                color: 'white',
                fontSize: '16px',
                padding: '12px',
                width: '100%'
              }}
              onClick={() => handleStatusClick('passed')}
              disabled={saving}
            >
              Прошел
            </button>
            <button
              className="btn"
              style={{
                flex: 1,
                background: '#f56565',
                color: 'white',
                fontSize: '16px',
                padding: '12px',
                width: '100%'
              }}
              onClick={() => handleStatusClick('failed')}
              disabled={saving}
            >
              Не прошел
            </button>
            <button
              className="btn"
              style={{
                flex: 1,
                background: '#ed8936',
                color: 'white',
                width: '100%',
                fontSize: '16px',
                padding: '12px'
              }}
              onClick={() => handleStatusClick('deferred')}
              disabled={saving}
            >
              Отложен
            </button>
          </div>
        )}
      </div>

      {/* Метаданные о поведении во время теста */}
      {results.background_switches_count > 0 || results.screenshots_count > 0 ? (
      <div className="card" style={{
        marginBottom: '20px', 
        background: results.background_switches_count > 0 || results.screenshots_count > 0
          ? 'linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%)'
          : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        border: results.background_switches_count > 0 || results.screenshots_count > 0
          ? '1px solid #feb2b2'
          : '1px solid #86efac'
      }}>
        <h3 style={{ marginBottom: '16px', fontSize: '16px', color: results.background_switches_count > 0 || results.screenshots_count > 0 ? '#c53030' : '#16a34a' }}>
          Метаданные о поведении во время теста
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {results.background_switches_count > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div>
                <div style={{ fontWeight: '600', color: '#742a2a' }}>
                  Сворачиваний/разворачиваний приложения:
                </div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#c53030' }}>
                  {results.background_switches_count}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div>
                <div style={{ fontWeight: '600', color: '#166534' }}>
                  Сворачиваний/разворачиваний приложения:
                </div>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#16a34a' }}>
                  Кандидат не сворачивал приложение
                </div>
              </div>
            </div>
          )}
          {results.screenshots_count > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div>
                <div style={{ fontWeight: '600', color: '#742a2a' }}>
                  Скриншотов сделано:
                </div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#c53030' }}>
                  {results.screenshots_count}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div>
                <div style={{ fontWeight: '600', color: '#166534' }}>
                  Скриншотов сделано:
                </div>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#16a34a' }}>
                  Кандидат не делал скриншоты
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      ) : (
        <div style={{ marginBottom: '20px' }}>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-100 text-green-800 text-sm font-semibold">
            <svg style={{width: 16, height: 16, display: 'inline-block', verticalAlign: '-2px'}} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            <span>Анти-чит: нарушений нет</span>
          </span>
        </div>
      )}

      {/* 1. Таблица сопоставлений - Desktop */}
      <div className="card">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px' }}>Проверка ответов</h2>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
            <input
              type="checkbox"
              checked={showWeightDescription}
              onChange={(e) => setShowWeightDescription(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Показать обоснование оценки
          </label>
        </div>
        
        {/* Desktop таблица */}
        <div style={{ overflowX: 'auto' }} className="hidden md:block">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ width: '40px' }}>#</th>
              <th>Вопрос</th>
              <th>Правильный ответ</th>
              <th>Ответ кандидата</th>
              <th style={{ width: '100px' }}>Результат</th>
              <th style={{ width: '100px', textAlign: 'center' }}>Баллы за правильный ответ</th>
              <th style={{ width: '120px', textAlign: 'center' }}>Баллы получено</th>
              {showWeightDescription && (
                <th style={{ width: '200px' }}>Обоснование оценки</th>
              )}
            </tr>
          </thead>
          <tbody>
            {results.questions.map((question, idx) => {
              const isCorrect = checks[question.question_id]?.is_correct;
              const isAuto = checks[question.question_id]?.auto;
              const earnedPoints = checks[question.question_id]?.earned_points || 0;
              
              // Определяем цвет подсветки для ответа кандидата
              let candidateAnswerBg = '#fff';
              let candidateAnswerBorder = '#e0e0e0';
              
              if (earnedPoints === question.weight && earnedPoints > 0) {
                // Полностью правильно - зеленый
                candidateAnswerBg = '#e6ffed';
                candidateAnswerBorder = '#48bb78';
              } else if (earnedPoints > 0 && earnedPoints < question.weight) {
                // Частично правильно - желтый
                candidateAnswerBg = '#fef5e7';
                candidateAnswerBorder = '#f59e0b';
              } else if (earnedPoints === 0 && question.candidate_answer) {
                // Есть ответ, но неправильно - светло-красный
                candidateAnswerBg = '#fee2e2';
                candidateAnswerBorder = '#ef4444';
              } else if (!question.candidate_answer) {
                // Нет ответа - желтый фон
                candidateAnswerBg = '#fef5e7';
                candidateAnswerBorder = '#e0e0e0';
              }

              return (
                <tr key={question.question_id}>
                  <td>{idx + 1}</td>
                  <td>
                    <div>
                      <strong>{question.question_text}</strong>
                      <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                        {question.section} | Вес: {question.weight} {question.question_type === 'single' && '(Авто)'}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ 
                      padding: '8px', 
                      background: '#f7fafc', 
                      borderRadius: '6px',
                      border: '1px solid #cbd5e0'
                    }}>
                      {formatAnswer(question.correct_answer, question.question_type, question.options)}
                    </div>
                  </td>
                  <td>
                    <div style={{ 
                      padding: '8px', 
                      background: candidateAnswerBg,
                      borderRadius: '6px',
                      border: `1px solid ${candidateAnswerBorder}`
                    }}>
                      {formatAnswer(question.candidate_answer, question.question_type, question.options)}
                    </div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      onClick={() => !isAuto && !isReadOnly && toggleCheck(question.question_id)}
                      disabled={isAuto || isReadOnly}
                      style={{
                        background: isCorrect ? '#48bb78' : '#f56565',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        cursor: (isAuto || isReadOnly) ? 'not-allowed' : 'pointer',
                        fontSize: '18px',
                        opacity: (isAuto || isReadOnly) ? 0.7 : 1
                      }}
                      title={isReadOnly ? 'Результаты зафиксированы' : (isAuto ? 'Автоматическая проверка' : 'Нажмите для изменения')}
                    >
                      {isCorrect ? <svg style={{width: 16, height: 16, display: 'inline-block', verticalAlign: '-2px'}} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg> : <svg style={{width: 16, height: 16, display: 'inline-block', verticalAlign: '-2px'}} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>}
                    </button>
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: '600', color: '#4a5568' }}>
                    {question.weight}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {isAuto ? (
                      <span style={{ 
                        fontWeight: '600',
                        color: checks[question.question_id]?.earned_points > 0 ? '#48bb78' : '#f56565'
                      }}>
                        {checks[question.question_id]?.earned_points || 0}
                      </span>
                    ) : (
                      <input
                        type="number"
                        min="0"
                        max={question.weight}
                        step="1"
                        value={checks[question.question_id]?.earned_points || 0}
                        onChange={(e) => updateEarnedPoints(question.question_id, e.target.value)}
                        disabled={isReadOnly}
                        title={`Оценка от 0 до ${question.weight} баллов`}
                        style={{
                          width: '60px',
                          padding: '4px 8px',
                          border: '1px solid #cbd5e0',
                          borderRadius: '4px',
                          textAlign: 'center',
                          fontSize: '14px',
                          fontWeight: '600',
                          backgroundColor: isReadOnly ? '#f7fafc' : '#fff',
                          cursor: isReadOnly ? 'not-allowed' : 'text'
                        }}
                      />
                    )}
                  </td>
                  {showWeightDescription && (
                    <td style={{ fontSize: '13px', color: '#718096', fontStyle: 'italic' }}>
                      {question.weight_description || '-'}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        
        {/* Mobile карточки */}
        <div className="md:hidden space-y-4">
          {results.questions.map((question, idx) => {
            const isCorrect = checks[question.question_id]?.is_correct;
            const isAuto = checks[question.question_id]?.auto;
            const earnedPoints = checks[question.question_id]?.earned_points || 0;
            
            // Определяем цвет подсветки для ответа кандидата
            let candidateAnswerBg = '#fff';
            let candidateAnswerBorder = '#e0e0e0';
            
            if (earnedPoints === question.weight && earnedPoints > 0) {
              candidateAnswerBg = '#e6ffed';
              candidateAnswerBorder = '#48bb78';
            } else if (earnedPoints > 0 && earnedPoints < question.weight) {
              candidateAnswerBg = '#fef5e7';
              candidateAnswerBorder = '#f59e0b';
            } else if (earnedPoints === 0 && question.candidate_answer) {
              candidateAnswerBg = '#fee2e2';
              candidateAnswerBorder = '#ef4444';
            } else if (!question.candidate_answer) {
              candidateAnswerBg = '#fef5e7';
              candidateAnswerBorder = '#e0e0e0';
            }

            return (
              <div key={question.question_id} style={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                padding: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                {/* Заголовок вопроса */}
                <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', minWidth: '30px' }}>#{idx + 1}</span>
                    <div style={{ flex: 1, marginLeft: '8px' }}>
                      <strong style={{ fontSize: '15px', lineHeight: '1.4', display: 'block', marginBottom: '4px' }}>
                        {question.question_text}
                      </strong>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                        {question.section} | Вес: {question.weight} {question.question_type === 'single' && '(Авто)'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Правильный ответ */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '6px' }}>
                    Правильный ответ:
                  </div>
                  <div style={{ 
                    padding: '10px', 
                    background: '#f7fafc', 
                    borderRadius: '8px',
                    border: '1px solid #cbd5e0',
                    fontSize: '14px',
                    wordBreak: 'break-word'
                  }}>
                    {formatAnswer(question.correct_answer, question.question_type, question.options)}
                  </div>
                </div>

                {/* Ответ кандидата */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '6px' }}>
                    Ответ кандидата:
                  </div>
                  <div style={{ 
                    padding: '10px', 
                    background: candidateAnswerBg,
                    borderRadius: '8px',
                    border: `1px solid ${candidateAnswerBorder}`,
                    fontSize: '14px',
                    wordBreak: 'break-word'
                  }}>
                    {formatAnswer(question.candidate_answer, question.question_type, question.options) || '(Нет ответа)'}
                  </div>
                </div>

                {/* Баллы и результат */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  paddingTop: '12px',
                  borderTop: '1px solid #e5e7eb',
                  gap: '12px',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                    <button
                      onClick={() => !isAuto && !isReadOnly && toggleCheck(question.question_id)}
                      disabled={isAuto || isReadOnly}
                      style={{
                        background: isCorrect ? '#48bb78' : '#f56565',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        cursor: (isAuto || isReadOnly) ? 'not-allowed' : 'pointer',
                        fontSize: '16px',
                        fontWeight: '600',
                        opacity: (isAuto || isReadOnly) ? 0.7 : 1,
                        minWidth: '44px'
                      }}
                      title={isReadOnly ? 'Результаты зафиксированы' : (isAuto ? 'Автоматическая проверка' : 'Нажмите для изменения')}
                    >
                      {isCorrect ? <svg style={{width: 16, height: 16, display: 'inline-block', verticalAlign: '-2px'}} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg> : <svg style={{width: 16, height: 16, display: 'inline-block', verticalAlign: '-2px'}} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>}
                    </button>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Результат</span>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                    <span style={{ color: '#6b7280' }}>Макс:</span>
                    <span style={{ fontWeight: '600', color: '#4a5568' }}>{question.weight}</span>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                    <span style={{ color: '#6b7280' }}>Получено:</span>
                    {isAuto ? (
                      <span style={{ 
                        fontWeight: '600',
                        color: earnedPoints > 0 ? '#48bb78' : '#f56565',
                        fontSize: '15px'
                      }}>
                        {earnedPoints}
                      </span>
                    ) : (
                      <input
                        type="number"
                        min="0"
                        max={question.weight}
                        step="1"
                        value={earnedPoints}
                        onChange={(e) => updateEarnedPoints(question.question_id, e.target.value)}
                        disabled={isReadOnly}
                        title={`Оценка от 0 до ${question.weight} баллов`}
                        style={{
                          width: '50px',
                          padding: '4px 6px',
                          border: '1px solid #cbd5e0',
                          borderRadius: '4px',
                          textAlign: 'center',
                          fontSize: '14px',
                          fontWeight: '600',
                          backgroundColor: isReadOnly ? '#f7fafc' : '#fff',
                          cursor: isReadOnly ? 'not-allowed' : 'text'
                        }}
                      />
                    )}
                  </div>
                </div>

                {/* Обоснование оценки (если включено) */}
                {showWeightDescription && question.weight_description && (
                  <div style={{ 
                    marginTop: '12px', 
                    paddingTop: '12px',
                    borderTop: '1px solid #e5e7eb',
                    fontSize: '12px', 
                    color: '#718096', 
                    fontStyle: 'italic'
                  }}>
                    <strong style={{ fontStyle: 'normal', color: '#6b7280' }}>Обоснование:</strong> {question.weight_description}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 2. Блок экспорта */}
      <div className="card" style={{ marginTop: '20px', background: '#f7fafc' }}>
        <p style={{ marginBottom: '16px', color: '#4a5568', lineHeight: '1.6', fontSize: '14px' }}>
          Для подтверждения результатов проверки ответственным специалистом можно экспортировать актуальный статус проверки
        </p>
        <div style={{ position: 'relative', display: 'inline-block', width: '100%' }} className="md:w-auto">
          <button 
            className="btn-primary w-full md:w-auto" 
            onClick={() => setShowExportMenu(!showExportMenu)}
            style={{ minWidth: '200px' }}
          >
            Экспортировать результаты
          </button>
          {showExportMenu && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '8px',
              background: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1000,
              minWidth: '200px'
            }}>
              <button
                onClick={() => exportResults('txt')}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'white',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f0f0f0',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => e.target.style.background = '#f7fafc'}
                onMouseLeave={(e) => e.target.style.background = 'white'}
              >
                Экспорт в TXT
              </button>
              <button
                onClick={() => exportResults('csv')}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'white',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px',
                  borderRadius: '0 0 8px 8px'
                }}
                onMouseEnter={(e) => e.target.style.background = '#f7fafc'}
                onMouseLeave={(e) => e.target.style.background = 'white'}
              >
                Экспорт в CSV
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Модальное окно с обратной связью (только для редактируемого режима) */}
      {showFeedback && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 99999,
            padding: '20px'
          }}
          onClick={(e) => {
            // Закрываем при клике на фон
            if (e.target === e.currentTarget) {
              setShowFeedback(false);
            }
          }}
        >
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
              <h2 style={{ margin: 0, color: '#2d3748', fontSize: '20px' }}>
                Предлагаемая форма обратной связи
            </h2>
              <button
                onClick={handleCopyFeedback}
                style={{
                  padding: '8px 16px',
                  backgroundColor: feedbackCopied ? '#10b981' : '#1e73e8',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap'
                }}
              >
                {feedbackCopied ? (
                  <>
                    <svg style={{width: 16, height: 16, display: 'inline-block', verticalAlign: '-2px'}} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                    <span>Скопировано</span>
                  </>
                ) : (
                  <>
                    <span>Скопировать</span>
                  </>
                )}
              </button>
            </div>
            <p style={{ marginBottom: '12px', color: '#666', fontSize: '14px', flexShrink: 0 }}>
              Скопируйте текст и отправьте кандидату:
            </p>
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              style={{
                width: '100%',
                height: '300px',
                padding: '16px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px',
                lineHeight: '1.8',
                fontFamily: 'inherit',
                resize: 'none',
                flex: 1,
                minHeight: '200px',
                maxHeight: '500px',
                overflow: 'auto'
              }}
            />
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px', flexShrink: 0 }}>
              <button
                className="btn-primary"
                onClick={handleConfirmStatus}
                disabled={saving}
                style={{ flex: 1 }}
              >
                {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button
                className="btn"
                onClick={() => setShowFeedback(false)}
                disabled={saving}
                style={{ flex: '0 0 120px' }}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
        , document.body
      )}
    </>
  );
}

export default TestResults;
